import { useState, useEffect, useCallback, useRef } from "react";
import { SidebarProvider } from "@/components/ui/sidebar";
import { DashboardSidebar } from "@/components/DashboardSidebar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Textarea } from "@/components/ui/textarea";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { GraftonChat } from "@/components/GraftonChat";
import { motion, AnimatePresence } from "framer-motion";
import jsPDF from "jspdf";
import {
  Printer, Mail, FileText, FolderOpen, Plus, Trash2, Upload,
  Send, Reply, Stamp, Archive, Eye, Sparkles, X, Check, Loader2,
  ChevronRight, Pencil, Building2, Landmark, Shield, MailOpen
} from "lucide-react";

// Types
interface MailFolder {
  id: string;
  user_id: string;
  name: string;
  color: string;
  icon: string;
  display_order: number;
  is_default: boolean;
}

interface MailDocument {
  id: string;
  user_id: string;
  folder_id: string | null;
  sender_name: string;
  sender_address: string;
  recipient_name: string;
  recipient_address: string;
  subject: string;
  content: string;
  file_url: string | null;
  status: string;
  received_date: string | null;
  analysis_summary: string | null;
  created_at: string;
}

const defaultFolders = [
  { name: "Boîte de réception", color: "hsl(220 70% 55%)", icon: "Inbox", display_order: 0, is_default: true },
  { name: "Courriers officiels", color: "hsl(0 84% 60%)", icon: "Stamp", display_order: 1, is_default: true },
  { name: "Factures & Devis", color: "hsl(160 84% 39%)", icon: "FileText", display_order: 2, is_default: true },
  { name: "Contrats", color: "hsl(270 76% 55%)", icon: "FileEdit", display_order: 3, is_default: true },
  { name: "Assurances", color: "hsl(25 95% 53%)", icon: "Shield", display_order: 4, is_default: true },
  { name: "Banque", color: "hsl(175 84% 32%)", icon: "Landmark", display_order: 5, is_default: true },
  { name: "Archives", color: "hsl(45 10% 50%)", icon: "Archive", display_order: 6, is_default: true },
];

const demoDocuments = [
  {
    sender_name: "URSSAF Rhône-Alpes",
    sender_address: "TSA 37000 - 69833 Saint-Priest Cedex 9",
    recipient_name: "Votre Entreprise",
    recipient_address: "12 rue de la République, 69001 Lyon",
    subject: "Appel de cotisations T2 2026",
    content: "Madame, Monsieur,\n\nNous vous informons que votre appel de cotisations pour le deuxième trimestre 2026 s'élève à 3 245,00 €.\n\nDate limite de paiement : 15 mai 2026\n\nRéférence : COT-2026-T2-78542\n\nVeuillez trouver ci-joint le détail de vos cotisations.\n\nCordialement,\nL'URSSAF Rhône-Alpes",
    status: "analyzed",
    received_date: "2026-03-28",
    analysis_summary: "Appel de cotisations URSSAF T2 2026 - Montant : 3 245 € - Échéance : 15/05/2026",
    folder_index: 0,
  },
  {
    sender_name: "AXA Assurances",
    sender_address: "313 Terrasses de l'Arche, 92727 Nanterre Cedex",
    recipient_name: "Votre Entreprise",
    recipient_address: "12 rue de la République, 69001 Lyon",
    subject: "Renouvellement assurance RC Pro",
    content: "Madame, Monsieur,\n\nVotre contrat de Responsabilité Civile Professionnelle n°AXA-RCP-2024-8547 arrive à échéance le 30 avril 2026.\n\nNouvelle prime annuelle : 1 180,00 € TTC\n\nMerci de nous retourner l'attestation signée avant le 20 avril.\n\nCordialement,\nVotre conseiller AXA",
    status: "new",
    received_date: "2026-03-27",
    analysis_summary: "Renouvellement RC Pro AXA - Prime : 1 180 € - Échéance signature : 20/04/2026",
    folder_index: 4,
  },
  {
    sender_name: "Greffe du Tribunal de Commerce",
    sender_address: "20 rue du Plat, 69002 Lyon",
    recipient_name: "Votre Entreprise",
    recipient_address: "12 rue de la République, 69001 Lyon",
    subject: "Confirmation dépôt des comptes annuels",
    content: "Madame, Monsieur,\n\nNous accusons réception du dépôt de vos comptes annuels pour l'exercice clos au 31 décembre 2025.\n\nRéférence de dépôt : DC-2026-LY-04521\n\nLe certificat de dépôt sera disponible sous 15 jours ouvrés.\n\nCordialement,\nLe Greffe du Tribunal de Commerce de Lyon",
    status: "analyzed",
    received_date: "2026-03-25",
    analysis_summary: "Confirmation dépôt comptes annuels 2025 - Réf: DC-2026-LY-04521",
    folder_index: 1,
  },
];

// ─── Scan Animation ─────────────────────────────────────────
const ScanAnimation = ({ isScanning, onScanComplete }: { isScanning: boolean; onScanComplete: () => void }) => {
  const [phase, setPhase] = useState<"idle" | "opening" | "letterOut" | "traveling" | "entering" | "scanning" | "done">("idle");

  useEffect(() => {
    if (isScanning) {
      setPhase("opening");
      const t1 = setTimeout(() => setPhase("letterOut"), 800);
      const t2 = setTimeout(() => setPhase("traveling"), 1600);
      const t3 = setTimeout(() => setPhase("entering"), 2800);
      const t4 = setTimeout(() => setPhase("scanning"), 3400);
      const t5 = setTimeout(() => { setPhase("done"); onScanComplete(); }, 5200);
      return () => { clearTimeout(t1); clearTimeout(t2); clearTimeout(t3); clearTimeout(t4); clearTimeout(t5); };
    } else {
      setPhase("idle");
    }
  }, [isScanning, onScanComplete]);

  const isActive = phase !== "idle" && phase !== "done";

  return (
    <div className="relative w-full h-56 md:h-64 overflow-hidden rounded-2xl bg-gradient-to-br from-secondary/40 via-background to-secondary/20 border border-border/50">
      {/* Subtle grid */}
      <div className="absolute inset-0 opacity-[0.03]"
        style={{ backgroundImage: "radial-gradient(circle, hsl(var(--primary)) 1px, transparent 1px)", backgroundSize: "24px 24px" }}
      />

      {/* Conveyor belt line */}
      <div className="absolute top-1/2 left-20 right-20 h-[2px] bg-border/40">
        {isActive && (
          <motion.div
            className="absolute inset-0 bg-gradient-to-r from-transparent via-primary/30 to-transparent"
            animate={{ x: ["-100%", "100%"] }}
            transition={{ duration: 1.5, repeat: Infinity, ease: "linear" }}
          />
        )}
      </div>

      <div className="relative flex items-center justify-between h-full px-10 md:px-20">
        {/* ── LEFT: Envelope ── */}
        <motion.div
          className="relative z-10"
          animate={
            phase === "traveling" || phase === "entering" || phase === "scanning" || phase === "done"
              ? { opacity: 0, scale: 0.7, x: 60 }
              : phase === "idle"
              ? { y: [0, -6, 0] }
              : {}
          }
          transition={
            phase === "idle"
              ? { duration: 3.5, repeat: Infinity, ease: "easeInOut" }
              : { duration: 0.6, ease: "easeIn" }
          }
        >
          {/* Envelope body */}
          <div className="relative w-28 h-[72px] md:w-36 md:h-[88px]">
            {/* Back of envelope */}
            <div className="absolute bottom-0 w-full h-14 md:h-[68px] rounded-lg bg-gradient-to-b from-amber-100 to-amber-200 dark:from-amber-900/50 dark:to-amber-800/50 border border-amber-300/50 dark:border-amber-700/40 shadow-lg" />
            
            {/* Flap - rotates open */}
            <motion.div
              className="absolute top-1 left-0 right-0 origin-bottom"
              style={{ perspective: 500, transformStyle: "preserve-3d" }}
              animate={
                phase === "opening" || phase === "letterOut"
                  ? { rotateX: -170 }
                  : { rotateX: 0 }
              }
              transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
            >
              <div
                className="w-full h-10 md:h-12 bg-gradient-to-b from-amber-300/80 to-amber-200 dark:from-amber-700/60 dark:to-amber-800/50 border border-amber-300/50 dark:border-amber-700/40 shadow-inner"
                style={{ clipPath: "polygon(0 100%, 50% 15%, 100% 100%)" }}
              />
            </motion.div>

            {/* Letter sliding out */}
            <motion.div
              className="absolute left-1/2 -translate-x-1/2 w-[78%] bg-card border border-border rounded-sm shadow-md overflow-hidden"
              style={{ bottom: "25%" }}
              animate={
                phase === "letterOut"
                  ? { y: -50, height: 56 }
                  : phase === "opening"
                  ? { y: -8, height: 28 }
                  : { y: 0, height: 22 }
              }
              transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
            >
              <div className="p-2 space-y-1">
                <div className="h-[3px] bg-primary/40 rounded w-3/4" />
                <div className="h-[2px] bg-muted-foreground/20 rounded w-full" />
                <div className="h-[2px] bg-muted-foreground/20 rounded w-5/6" />
                <div className="h-[2px] bg-muted-foreground/20 rounded w-2/3" />
                <div className="h-[2px] bg-muted-foreground/20 rounded w-full" />
              </div>
            </motion.div>

            {/* Stamp */}
            <div className="absolute top-3 right-2 w-5 h-6 md:w-6 md:h-7 rounded-sm border border-amber-400/40 bg-amber-50/60 dark:bg-amber-900/30 flex items-center justify-center">
              <Stamp className="h-3 w-3 text-amber-600/60 dark:text-amber-400/40" />
            </div>
          </div>
        </motion.div>

        {/* ── MIDDLE: Traveling letter ── */}
        <AnimatePresence>
          {(phase === "traveling" || phase === "entering") && (
            <motion.div
              className="absolute z-20 left-[15%]"
              initial={{ x: 0, y: 0, opacity: 1, rotate: -3 }}
              animate={
                phase === "entering"
                  ? { x: "calc(70vw - 200px)", y: 0, opacity: 0, rotate: 0, scale: 0.8 }
                  : { x: "calc(35vw - 100px)", y: -10, opacity: 1, rotate: 1 }
              }
              exit={{ opacity: 0 }}
              transition={{ duration: phase === "entering" ? 0.6 : 1.1, ease: [0.22, 1, 0.36, 1] }}
            >
              <div className="w-24 h-16 md:w-32 md:h-20 bg-card border border-border rounded shadow-xl overflow-hidden">
                <div className="p-2 space-y-1">
                  <div className="h-[3px] bg-primary/40 rounded w-3/4" />
                  <div className="h-[2px] bg-muted-foreground/20 rounded w-full" />
                  <div className="h-[2px] bg-muted-foreground/20 rounded w-5/6" />
                  <div className="h-[2px] bg-muted-foreground/20 rounded w-2/3" />
                  <div className="h-[2px] bg-muted-foreground/15 rounded w-full" />
                  <div className="h-[2px] bg-muted-foreground/15 rounded w-4/5" />
                </div>
              </div>
              {/* Shadow underneath */}
              <div className="absolute -bottom-2 left-1/2 -translate-x-1/2 w-20 h-2 bg-black/10 dark:bg-black/20 rounded-full blur-sm" />
            </motion.div>
          )}
        </AnimatePresence>

        {/* ── RIGHT: Industrial Scanner ── */}
        <motion.div
          className="relative z-10"
          animate={phase === "scanning" ? { scale: [1, 1.01, 1] } : {}}
          transition={{ duration: 0.4, repeat: phase === "scanning" ? Infinity : 0 }}
        >
          <div className="relative">
            {/* Main body */}
            <div className="w-36 h-28 md:w-48 md:h-32 rounded-xl bg-gradient-to-b from-muted-foreground/10 to-secondary border-2 border-border shadow-2xl relative overflow-hidden">
              {/* Top feed slot */}
              <div className="absolute top-0 left-1/2 -translate-x-1/2 w-24 md:w-32 h-3 bg-muted-foreground/20 rounded-b-lg border-x border-b border-border/50">
                {(phase === "entering" || phase === "scanning") && (
                  <motion.div
                    className="absolute inset-x-0 top-0 h-full bg-primary/20"
                    animate={{ opacity: [0.3, 0.8, 0.3] }}
                    transition={{ duration: 0.5, repeat: Infinity }}
                  />
                )}
              </div>

              {/* LCD Display */}
              <div className="absolute top-5 left-3 w-14 md:w-20 h-7 rounded bg-black/20 dark:bg-black/40 border border-border/50 flex items-center justify-center overflow-hidden">
                {phase === "scanning" ? (
                  <motion.div className="flex gap-[3px]">
                    {[0, 1, 2, 3, 4].map((i) => (
                      <motion.div
                        key={i}
                        className="w-[3px] h-4 bg-primary rounded-full"
                        animate={{ scaleY: [0.3, 1, 0.3] }}
                        transition={{ duration: 0.5, repeat: Infinity, delay: i * 0.1 }}
                      />
                    ))}
                  </motion.div>
                ) : phase === "entering" ? (
                  <motion.span
                    className="text-[8px] text-primary font-mono"
                    animate={{ opacity: [0.5, 1, 0.5] }}
                    transition={{ duration: 0.8, repeat: Infinity }}
                  >
                    LOADING
                  </motion.span>
                ) : (
                  <span className="text-[8px] text-green-500 font-mono">READY</span>
                )}
              </div>

              {/* Status LEDs */}
              <div className="absolute top-5 right-3 flex flex-col gap-1.5">
                <div className={`h-2.5 w-2.5 rounded-full border border-border/50 ${phase === "scanning" ? "bg-green-500 shadow-[0_0_6px_rgba(34,197,94,0.5)]" : "bg-green-500/40"}`} />
                <motion.div
                  className="h-2.5 w-2.5 rounded-full border border-border/50 bg-primary"
                  animate={isActive ? { opacity: [0.4, 1, 0.4] } : { opacity: 0.4 }}
                  transition={{ duration: 0.6, repeat: isActive ? Infinity : 0 }}
                />
                <div className="h-2.5 w-2.5 rounded-full border border-border/50 bg-muted-foreground/20" />
              </div>

              {/* Scanner glass area */}
              <div className="absolute top-14 left-3 right-3 h-6 rounded bg-muted/60 border border-border/40 overflow-hidden">
                {phase === "scanning" && (
                  <motion.div
                    className="h-full w-2 bg-gradient-to-r from-primary/0 via-primary/80 to-primary/0 shadow-[0_0_12px_rgba(var(--primary),0.3)]"
                    animate={{ x: [-10, 160] }}
                    transition={{ duration: 0.7, repeat: Infinity, ease: "linear" }}
                  />
                )}
              </div>

              {/* Output tray */}
              <div className="absolute bottom-2 left-3 right-3 h-5 rounded bg-muted-foreground/5 border border-border/30 flex items-center justify-between px-2">
                <div className="flex gap-[2px]">
                  {[...Array(6)].map((_, i) => (
                    <div key={i} className="w-[2px] h-3 bg-border/60 rounded" />
                  ))}
                </div>
                <span className="text-[7px] text-muted-foreground/50 font-mono tracking-wider">GRAFTON</span>
              </div>
            </div>

            {/* Side panel detail */}
            <div className="absolute -right-1 top-4 w-2 h-16 rounded-r bg-border/40" />

            {/* Label */}
            <div className="absolute -bottom-4 left-1/2 -translate-x-1/2 px-3 py-0.5 rounded-full bg-secondary/80 border border-border/30">
              <span className="text-[8px] text-muted-foreground/60 font-mono tracking-widest whitespace-nowrap">GRAFTON SCANNER PRO</span>
            </div>
          </div>
        </motion.div>
      </div>

      {/* Status bar */}
      <div className="absolute bottom-4 left-1/2 -translate-x-1/2">
        <AnimatePresence mode="wait">
          {isActive ? (
            <motion.div
              key="active"
              className="flex items-center gap-2 px-5 py-2 rounded-full bg-primary/10 border border-primary/20 backdrop-blur-sm"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
            >
              <Loader2 className="h-3.5 w-3.5 text-primary animate-spin" />
              <span className="text-xs text-primary font-medium">
                {phase === "opening" && "Ouverture de l'enveloppe…"}
                {phase === "letterOut" && "Extraction du courrier…"}
                {phase === "traveling" && "Envoi vers le scanner…"}
                {phase === "entering" && "Insertion dans le scanner…"}
                {phase === "scanning" && "Grafton analyse le document…"}
              </span>
            </motion.div>
          ) : (
            <motion.div
              key="idle"
              className="flex items-center gap-2 px-5 py-2 rounded-full bg-secondary/60 border border-border/40 backdrop-blur-sm"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
            >
              <Upload className="h-3.5 w-3.5 text-muted-foreground" />
              <span className="text-xs text-muted-foreground">Importez un courrier pour l'analyser</span>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

// ─── Main Component ─────────────────────────────────────────
const AdminDashboard = () => {
  const { user } = useAuth();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [folders, setFolders] = useState<MailFolder[]>([]);
  const [documents, setDocuments] = useState<MailDocument[]>([]);
  const [selectedFolder, setSelectedFolder] = useState<string | null>(null);
  const [selectedDoc, setSelectedDoc] = useState<MailDocument | null>(null);
  const [loading, setLoading] = useState(true);
  const [isScanning, setIsScanning] = useState(false);
  const [scanComplete, setScanComplete] = useState(false);

  const [newFolderDialog, setNewFolderDialog] = useState(false);
  const [newFolderName, setNewFolderName] = useState("");
  const [editingFolder, setEditingFolder] = useState<string | null>(null);
  const [editFolderName, setEditFolderName] = useState("");

  const [replyDialog, setReplyDialog] = useState(false);
  const [replyMode, setReplyMode] = useState<"email" | "postal">("email");
  const [replyBody, setReplyBody] = useState("");
  const [generatingReply, setGeneratingReply] = useState(false);

  const [classifyDialog, setClassifyDialog] = useState(false);
  const [recommandeDialog, setRecommandeDialog] = useState(false);

  // Track the doc being analyzed for AI update
  const pendingDocRef = useRef<string | null>(null);

  const loadData = useCallback(async () => {
    if (!user) return;
    setLoading(true);

    const { data: foldersData } = await supabase
      .from("mail_folders")
      .select("*")
      .eq("user_id", user.id)
      .order("display_order");

    if (!foldersData || foldersData.length === 0) {
      const inserts = defaultFolders.map((f) => ({ ...f, user_id: user.id }));
      const { data: newFolders } = await supabase.from("mail_folders").insert(inserts).select();
      if (newFolders) {
        setFolders(newFolders as MailFolder[]);
        const docInserts = ([] as typeof demoDocuments).map((d) => ({
          user_id: user.id,
          folder_id: newFolders[d.folder_index]?.id || newFolders[0]?.id,
          sender_name: d.sender_name,
          sender_address: d.sender_address,
          recipient_name: d.recipient_name,
          recipient_address: d.recipient_address,
          subject: d.subject,
          content: d.content,
          status: d.status,
          received_date: d.received_date,
          analysis_summary: d.analysis_summary,
        }));
        const { data: newDocs } = await supabase.from("mail_documents").insert(docInserts).select();
        if (newDocs) setDocuments(newDocs as MailDocument[]);
      }
    } else {
      setFolders(foldersData as MailFolder[]);
      const { data: docsData } = await supabase
        .from("mail_documents")
        .select("*")
        .eq("user_id", user.id)
        .order("created_at", { ascending: false });
      if (docsData) setDocuments(docsData as MailDocument[]);
    }
    setLoading(false);
  }, [user]);

  useEffect(() => { loadData(); }, [loadData]);

  // ── Folder CRUD ──
  const addFolder = async () => {
    if (!user || !newFolderName.trim()) return;
    const { data, error } = await supabase
      .from("mail_folders")
      .insert({ user_id: user.id, name: newFolderName.trim(), display_order: folders.length })
      .select()
      .single();
    if (error) { toast.error("Erreur lors de la création"); return; }
    setFolders((prev) => [...prev, data as MailFolder]);
    setNewFolderName("");
    setNewFolderDialog(false);
    toast.success(`Dossier "${newFolderName}" créé`);
  };

  const deleteFolder = async (id: string) => {
    if (!user) return;
    const inbox = folders.find((f) => f.display_order === 0);
    if (inbox) {
      await supabase.from("mail_documents").update({ folder_id: inbox.id }).eq("folder_id", id).eq("user_id", user.id);
    }
    await supabase.from("mail_folders").delete().eq("id", id).eq("user_id", user.id);
    setFolders((prev) => prev.filter((f) => f.id !== id));
    setDocuments((prev) => prev.map((d) => (d.folder_id === id ? { ...d, folder_id: inbox?.id || null } : d)));
    if (selectedFolder === id) setSelectedFolder(null);
    toast.success("Dossier supprimé");
  };

  const renameFolder = async (id: string) => {
    if (!editFolderName.trim()) return;
    await supabase.from("mail_folders").update({ name: editFolderName.trim() }).eq("id", id);
    setFolders((prev) => prev.map((f) => (f.id === id ? { ...f, name: editFolderName.trim() } : f)));
    setEditingFolder(null);
    toast.success("Dossier renommé");
  };

  const classifyDoc = async (folderId: string) => {
    if (!selectedDoc) return;
    await supabase.from("mail_documents").update({ folder_id: folderId, status: "classified" }).eq("id", selectedDoc.id);
    setDocuments((prev) => prev.map((d) => (d.id === selectedDoc.id ? { ...d, folder_id: folderId, status: "classified" } : d)));
    setClassifyDialog(false);
    toast.success("Courrier classé !");
  };

  // ── File Upload + AI Analysis ──
  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !user) return;

    setIsScanning(true);
    setScanComplete(false);

    const filePath = `${user.id}/mail/${Date.now()}-${file.name}`;
    const { error: uploadError } = await supabase.storage.from("company-documents").upload(filePath, file);

    if (uploadError) {
      toast.error("Erreur lors de l'upload");
      setIsScanning(false);
      return;
    }

    const { data: urlData } = supabase.storage.from("company-documents").getPublicUrl(filePath);

    const { data: newDoc } = await supabase.from("mail_documents").insert({
      user_id: user.id,
      folder_id: folders[0]?.id || null,
      sender_name: "En cours d'analyse…",
      subject: file.name.replace(/\.[^.]+$/, ""),
      content: "Le contenu de ce courrier est en cours d'analyse par Grafton IA…",
      file_url: urlData.publicUrl,
      status: "analyzing",
      received_date: new Date().toISOString().split("T")[0],
    }).select().single();

    if (newDoc) {
      setDocuments((prev) => [newDoc as MailDocument, ...prev]);
      pendingDocRef.current = newDoc.id;

      // Trigger AI analysis
      try {
        const { data: aiData } = await supabase.functions.invoke("grafton-chat", {
          body: {
            message: `Analyse ce courrier administratif. Le fichier s'appelle "${file.name}". 
Extrais et retourne les informations suivantes en JSON entre les balises <!--MAIL_ANALYSIS_START--> et <!--MAIL_ANALYSIS_END--> :
{
  "sender_name": "Nom de l'expéditeur",
  "sender_address": "Adresse complète",
  "subject": "Objet du courrier",
  "summary": "Résumé en une phrase avec montants et dates clés",
  "suggested_folder": "Nom du dossier suggéré parmi: Courriers officiels, Factures & Devis, Contrats, Assurances, Banque"
}
Si tu ne peux pas analyser le fichier, génère un exemple réaliste basé sur le nom du fichier.`,
            context: { page: "administratif" },
            stream: false,
          },
        });

        if (aiData?.reply) {
          const match = aiData.reply.match(/<!--MAIL_ANALYSIS_START-->([\s\S]*?)<!--MAIL_ANALYSIS_END-->/);
          let analysis = null;
          if (match) {
            try { analysis = JSON.parse(match[1].trim()); } catch { /* ignore parse error */ }
          }

          const updates: Record<string, string> = {
            status: "analyzed",
            analysis_summary: analysis?.summary || "Document analysé par Grafton IA",
          };
          if (analysis?.sender_name) updates.sender_name = analysis.sender_name;
          if (analysis?.sender_address) updates.sender_address = analysis.sender_address;
          if (analysis?.subject) updates.subject = analysis.subject;

          // Find suggested folder
          if (analysis?.suggested_folder) {
            const suggestedFolder = folders.find((f) => f.name.toLowerCase().includes(analysis.suggested_folder.toLowerCase()));
            if (suggestedFolder) updates.folder_id = suggestedFolder.id;
          }

          await supabase.from("mail_documents").update(updates).eq("id", newDoc.id);
          setDocuments((prev) =>
            prev.map((d) => (d.id === newDoc.id ? { ...d, ...updates } as MailDocument : d))
          );
        }
      } catch (err) {
        console.error("AI analysis error:", err);
        await supabase.from("mail_documents").update({ status: "new", sender_name: "Document importé" }).eq("id", newDoc.id);
        setDocuments((prev) =>
          prev.map((d) => (d.id === newDoc.id ? { ...d, status: "new", sender_name: "Document importé" } : d))
        );
      }
    }

    // Reset file input
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const onScanComplete = useCallback(() => {
    setIsScanning(false);
    setScanComplete(true);
    toast.success("Courrier analysé par Grafton IA !");
    setTimeout(() => setScanComplete(false), 3000);
  }, []);

  // ── AI-assisted reply ──
  const generateAiReply = async () => {
    if (!selectedDoc) return;
    setGeneratingReply(true);
    try {
      const { data } = await supabase.functions.invoke("grafton-chat", {
        body: {
          message: `Rédige une réponse professionnelle et concise à ce courrier :
Expéditeur : ${selectedDoc.sender_name}
Objet : ${selectedDoc.subject}
Contenu : ${selectedDoc.content}

${replyMode === "postal" ? "Format le texte comme un courrier postal formel avec en-tête, date, formule de politesse." : "Rédige un email professionnel court."}`,
          context: { page: "administratif" },
          stream: false,
        },
      });
      if (data?.reply) setReplyBody(data.reply);
    } catch {
      toast.error("Erreur lors de la génération");
    }
    setGeneratingReply(false);
  };

  // ── Generate postal PDF ──
  const generatePostalPdf = () => {
    if (!selectedDoc || !replyBody) return;

    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    const margin = 25;
    const maxWidth = pageWidth - margin * 2;

    // Sender info (top left)
    doc.setFontSize(10);
    doc.text("Votre Entreprise", margin, 30);
    doc.setFontSize(9);
    doc.text("12 rue de la République", margin, 36);
    doc.text("69001 Lyon", margin, 42);

    // Recipient (top right)
    doc.setFontSize(10);
    doc.text(selectedDoc.sender_name, pageWidth - margin, 50, { align: "right" });
    doc.setFontSize(9);
    const recipientLines = doc.splitTextToSize(selectedDoc.sender_address, 70);
    recipientLines.forEach((line: string, i: number) => {
      doc.text(line, pageWidth - margin, 56 + i * 5, { align: "right" });
    });

    // Date
    doc.setFontSize(9);
    doc.text(
      `Lyon, le ${new Date().toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" })}`,
      pageWidth - margin, 80, { align: "right" }
    );

    // Subject
    doc.setFontSize(10);
    doc.setFont("helvetica", "bold");
    doc.text(`Objet : RE - ${selectedDoc.subject}`, margin, 95);
    doc.setFont("helvetica", "normal");

    // Body
    doc.setFontSize(10);
    const bodyLines = doc.splitTextToSize(replyBody, maxWidth);
    let y = 110;
    bodyLines.forEach((line: string) => {
      if (y > 270) { doc.addPage(); y = 30; }
      doc.text(line, margin, y);
      y += 5.5;
    });

    doc.save(`reponse-${selectedDoc.subject.substring(0, 30).replace(/[^a-zA-Z0-9]/g, "-")}.pdf`);
    toast.success("PDF généré et téléchargé !");
  };

  const handleReply = () => {
    if (replyMode === "email") {
      // Open mailto with pre-filled content
      const email = `contact@${selectedDoc?.sender_name.toLowerCase().replace(/\s+/g, "-").replace(/[^a-z0-9-]/g, "")}.fr`;
      const subject = encodeURIComponent(`RE: ${selectedDoc?.subject || ""}`);
      const body = encodeURIComponent(replyBody);
      window.open(`mailto:${email}?subject=${subject}&body=${body}`);
      toast.success("Client email ouvert avec votre réponse !");
    } else {
      generatePostalPdf();
    }
    setReplyDialog(false);
    setReplyBody("");
  };

  const filteredDocs = selectedFolder
    ? documents.filter((d) => d.folder_id === selectedFolder)
    : documents;

  const getStatusBadge = (status: string) => {
    switch (status) {
      case "new": return <Badge className="bg-blue-500/20 text-blue-400 border-blue-500/30">Nouveau</Badge>;
      case "analyzed": return <Badge className="bg-primary/20 text-primary border-primary/30">Analysé</Badge>;
      case "classified": return <Badge className="bg-green-500/20 text-green-400 border-green-500/30">Classé</Badge>;
      case "analyzing": return <Badge className="bg-purple-500/20 text-purple-400 border-purple-500/30 animate-pulse">En analyse…</Badge>;
      default: return <Badge variant="secondary">{status}</Badge>;
    }
  };

  if (loading) {
    return (
      <SidebarProvider>
        <div className="min-h-screen flex w-full bg-background">
          <DashboardSidebar />
          <main className="flex-1 flex items-center justify-center">
            <Loader2 className="h-8 w-8 text-primary animate-spin" />
          </main>
        </div>
      </SidebarProvider>
    );
  }

  return (
    <SidebarProvider>
      <div className="min-h-screen flex w-full bg-background">
        <DashboardSidebar />
        <main className="flex-1 overflow-y-auto">
          <div className="p-4 md:p-6 max-w-7xl mx-auto space-y-6">
            {/* Header */}
            <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
              <div>
                <h1 className="text-2xl font-bold text-foreground flex items-center gap-2">
                  <Stamp className="h-6 w-6 text-primary" />
                  Administratif & Courrier
                </h1>
                <p className="text-sm text-muted-foreground mt-1">
                  Numérisez, analysez et gérez vos courriers papier avec Grafton IA
                </p>
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                <Button onClick={() => fileInputRef.current?.click()} className="gap-2">
                  <Upload className="h-4 w-4" />
                  Importer un courrier
                </Button>
                <Button variant="outline" className="gap-2" onClick={() => toast.info("Connexion URSSAF — intégration prochainement disponible")}>
                  <Building2 className="h-4 w-4" />
                  URSSAF
                </Button>
                <Button variant="outline" className="gap-2" onClick={() => toast.info("Connexion Impôts — intégration prochainement disponible")}>
                  <Landmark className="h-4 w-4" />
                  Impôts
                </Button>
                <Button variant="outline" className="gap-2" onClick={() => setRecommandeDialog(true)}>
                  <Shield className="h-4 w-4" />
                  Envoyer un recommandé
                </Button>
              </div>
              <input
                ref={fileInputRef}
                type="file"
                accept=".pdf,.jpg,.jpeg,.png,.webp"
                className="hidden"
                onChange={handleFileUpload}
              />
            </div>

            {/* Scan Animation */}
            <ScanAnimation isScanning={isScanning} onScanComplete={onScanComplete} />

            {/* Main content */}
            <div className="flex gap-4">
              {/* Left: Folder sidebar */}
              <div className="w-56 shrink-0 space-y-2">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-sm font-semibold text-foreground">Dossiers</h3>
                  <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => setNewFolderDialog(true)}>
                    <Plus className="h-4 w-4" />
                  </Button>
                </div>

                <button
                  onClick={() => setSelectedFolder(null)}
                  className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-all ${
                    !selectedFolder ? "bg-primary/10 text-primary border border-primary/20" : "text-muted-foreground hover:bg-secondary/50"
                  }`}
                >
                  <FolderOpen className="h-4 w-4" />
                  <span className="flex-1 text-left truncate">Tous les courriers</span>
                  <span className="text-xs opacity-60">{documents.length}</span>
                </button>

                <Separator className="my-2" />

                <ScrollArea className="h-[calc(100vh-560px)]">
                  <div className="space-y-0.5 pr-2">
                    {folders.map((folder) => {
                      const count = documents.filter((d) => d.folder_id === folder.id).length;
                      return (
                        <div key={folder.id} className="group relative">
                          {editingFolder === folder.id ? (
                            <div className="flex gap-1">
                              <Input
                                value={editFolderName}
                                onChange={(e) => setEditFolderName(e.target.value)}
                                className="h-8 text-xs"
                                onKeyDown={(e) => e.key === "Enter" && renameFolder(folder.id)}
                                autoFocus
                              />
                              <Button size="icon" variant="ghost" className="h-8 w-8 shrink-0" onClick={() => renameFolder(folder.id)}>
                                <Check className="h-3 w-3" />
                              </Button>
                              <Button size="icon" variant="ghost" className="h-8 w-8 shrink-0" onClick={() => setEditingFolder(null)}>
                                <X className="h-3 w-3" />
                              </Button>
                            </div>
                          ) : (
                            <button
                              onClick={() => setSelectedFolder(folder.id)}
                              className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-all ${
                                selectedFolder === folder.id ? "bg-primary/10 text-primary border border-primary/20" : "text-muted-foreground hover:bg-secondary/50"
                              }`}
                            >
                              <div className="h-2.5 w-2.5 rounded-full shrink-0" style={{ backgroundColor: folder.color }} />
                              <span className="flex-1 text-left truncate">{folder.name}</span>
                              <span className="text-xs opacity-60">{count}</span>
                              <div className="hidden group-hover:flex items-center gap-0.5 ml-1">
                                <button
                                  onClick={(e) => { e.stopPropagation(); setEditingFolder(folder.id); setEditFolderName(folder.name); }}
                                  className="p-0.5 rounded hover:bg-muted"
                                >
                                  <Pencil className="h-3 w-3" />
                                </button>
                                {!folder.is_default && (
                                  <button
                                    onClick={(e) => { e.stopPropagation(); deleteFolder(folder.id); }}
                                    className="p-0.5 rounded hover:bg-destructive/20 text-destructive"
                                  >
                                    <Trash2 className="h-3 w-3" />
                                  </button>
                                )}
                              </div>
                            </button>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </ScrollArea>
              </div>

              {/* Center: Document list + viewer */}
              <div className="flex-1 min-w-0">
                {selectedDoc ? (
                  <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="space-y-4">
                    <Button variant="ghost" size="sm" onClick={() => setSelectedDoc(null)} className="gap-1 text-muted-foreground">
                      <ChevronRight className="h-4 w-4 rotate-180" /> Retour
                    </Button>

                    <div className="flex gap-4">
                      {/* Letter card */}
                      <Card className="flex-1 border-border/50">
                        <div className="p-8">
                          <div className="flex justify-between mb-8">
                            <div>
                              <p className="text-sm font-semibold text-foreground">{selectedDoc.sender_name}</p>
                              <p className="text-xs text-muted-foreground whitespace-pre-line">{selectedDoc.sender_address}</p>
                            </div>
                            <div className="text-right">
                              <p className="text-sm font-semibold text-foreground">{selectedDoc.recipient_name}</p>
                              <p className="text-xs text-muted-foreground whitespace-pre-line">{selectedDoc.recipient_address}</p>
                            </div>
                          </div>
                          <div className="mb-6">
                            {selectedDoc.received_date && (
                              <p className="text-xs text-muted-foreground mb-1">
                                Reçu le {new Date(selectedDoc.received_date).toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" })}
                              </p>
                            )}
                            <h2 className="text-lg font-semibold text-foreground">{selectedDoc.subject}</h2>
                            {getStatusBadge(selectedDoc.status)}
                          </div>
                          <Separator className="mb-6" />
                          <div className="whitespace-pre-line text-sm text-foreground/90 leading-relaxed">
                            {selectedDoc.content}
                          </div>
                          {selectedDoc.analysis_summary && (
                            <div className="mt-6 p-4 rounded-lg bg-primary/5 border border-primary/20">
                              <div className="flex items-center gap-2 mb-2">
                                <Sparkles className="h-4 w-4 text-primary" />
                                <span className="text-sm font-medium text-primary">Analyse Grafton IA</span>
                              </div>
                              <p className="text-sm text-foreground/80">{selectedDoc.analysis_summary}</p>
                            </div>
                          )}
                        </div>
                      </Card>

                      {/* Action buttons */}
                      <div className="w-48 shrink-0 space-y-2">
                        <Button className="w-full justify-start gap-2" onClick={() => { setReplyMode("email"); setReplyDialog(true); }}>
                          <Reply className="h-4 w-4" />
                          Répondre par email
                        </Button>
                        <Button variant="outline" className="w-full justify-start gap-2" onClick={() => { setReplyMode("postal"); setReplyDialog(true); }}>
                          <Stamp className="h-4 w-4" />
                          Répondre par courrier
                        </Button>
                        <Button variant="outline" className="w-full justify-start gap-2" onClick={() => setClassifyDialog(true)}>
                          <Archive className="h-4 w-4" />
                          Classer ce document
                        </Button>
                        <Button variant="outline" className="w-full justify-start gap-2" onClick={() => setRecommandeDialog(true)}>
                          <Shield className="h-4 w-4" />
                          Envoyer en recommandé
                        </Button>
                        {selectedDoc.file_url && (
                          <Button variant="secondary" className="w-full justify-start gap-2" onClick={() => window.open(selectedDoc.file_url!, "_blank")}>
                            <Eye className="h-4 w-4" />
                            Voir l'original
                          </Button>
                        )}
                      </div>
                    </div>
                  </motion.div>
                ) : (
                  <div className="space-y-2">
                    <div className="flex items-center justify-between mb-3">
                      <h3 className="text-sm font-semibold text-foreground">
                        {selectedFolder ? folders.find((f) => f.id === selectedFolder)?.name : "Tous les courriers"}{" "}
                        <span className="text-muted-foreground font-normal">({filteredDocs.length})</span>
                      </h3>
                    </div>

                    {filteredDocs.length === 0 ? (
                      <Card className="border-dashed border-border/50">
                        <div className="py-12 flex flex-col items-center gap-3 text-center">
                          <Mail className="h-10 w-10 text-muted-foreground/40" />
                          <p className="text-sm text-muted-foreground">Aucun courrier dans ce dossier</p>
                          <Button variant="outline" size="sm" onClick={() => fileInputRef.current?.click()}>
                            <Upload className="h-4 w-4 mr-2" />
                            Importer un courrier
                          </Button>
                        </div>
                      </Card>
                    ) : (
                      <ScrollArea className="h-[calc(100vh-500px)]">
                        <div className="space-y-2 pr-2">
                          {filteredDocs.map((doc) => {
                            const folder = folders.find((f) => f.id === doc.folder_id);
                            return (
                              <motion.div key={doc.id} initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }}>
                                <Card className="cursor-pointer hover:border-primary/30 transition-all border-border/50" onClick={() => setSelectedDoc(doc)}>
                                  <div className="p-4">
                                    <div className="flex items-start gap-3">
                                      <div className="h-8 w-8 rounded-lg bg-secondary flex items-center justify-center shrink-0 mt-0.5">
                                        <FileText className="h-4 w-4 text-muted-foreground" />
                                      </div>
                                      <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-2 mb-0.5">
                                          <span className="text-sm font-medium text-foreground truncate">{doc.sender_name}</span>
                                          {getStatusBadge(doc.status)}
                                        </div>
                                        <p className="text-sm text-foreground/80 truncate">{doc.subject}</p>
                                        {doc.analysis_summary && (
                                          <p className="text-xs text-muted-foreground mt-1 truncate flex items-center gap-1">
                                            <Sparkles className="h-3 w-3 text-primary shrink-0" />
                                            {doc.analysis_summary}
                                          </p>
                                        )}
                                      </div>
                                      <div className="text-right shrink-0">
                                        {doc.received_date && (
                                          <span className="text-xs text-muted-foreground">
                                            {new Date(doc.received_date).toLocaleDateString("fr-FR")}
                                          </span>
                                        )}
                                        {folder && (
                                          <div className="flex items-center gap-1 mt-1 justify-end">
                                            <div className="h-2 w-2 rounded-full" style={{ backgroundColor: folder.color }} />
                                            <span className="text-xs text-muted-foreground">{folder.name}</span>
                                          </div>
                                        )}
                                      </div>
                                    </div>
                                  </div>
                                </Card>
                              </motion.div>
                            );
                          })}
                        </div>
                      </ScrollArea>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Grafton Chat */}
            <GraftonChat
              context={{ page: "administratif", documents: documents.length, folders: folders.length }}
              quickPrompts={[
                "Analyse ce courrier et résume les actions à prendre",
                "Rédige une réponse professionnelle à ce courrier",
                "Quels sont les délais importants dans mes courriers ?",
                "Classe automatiquement mes courriers non traités",
              ]}
            />
          </div>
        </main>
      </div>

      {/* New folder dialog */}
      <Dialog open={newFolderDialog} onOpenChange={setNewFolderDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Nouveau dossier</DialogTitle>
          </DialogHeader>
          <Input
            placeholder="Nom du dossier"
            value={newFolderName}
            onChange={(e) => setNewFolderName(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && addFolder()}
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setNewFolderDialog(false)}>Annuler</Button>
            <Button onClick={addFolder} disabled={!newFolderName.trim()}>Créer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reply dialog */}
      <Dialog open={replyDialog} onOpenChange={setReplyDialog}>
        <DialogContent className="max-w-xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {replyMode === "email" ? <Reply className="h-5 w-5 text-primary" /> : <Stamp className="h-5 w-5 text-primary" />}
              {replyMode === "email" ? "Répondre par email" : "Répondre par courrier postal (PDF)"}
            </DialogTitle>
          </DialogHeader>
          {selectedDoc && (
            <div className="space-y-4">
              <div className="p-3 rounded-lg bg-secondary/50 text-sm space-y-1">
                <p><span className="text-muted-foreground">À :</span> {selectedDoc.sender_name}</p>
                {replyMode === "email" ? (
                  <p><span className="text-muted-foreground">Email :</span> contact@{selectedDoc.sender_name.toLowerCase().replace(/\s+/g, "-").replace(/[^a-z0-9-]/g, "")}.fr</p>
                ) : (
                  <p><span className="text-muted-foreground">Adresse :</span> {selectedDoc.sender_address}</p>
                )}
                <p><span className="text-muted-foreground">Objet :</span> RE: {selectedDoc.subject}</p>
              </div>
              <Textarea
                placeholder="Rédigez votre réponse ou utilisez Grafton IA…"
                value={replyBody}
                onChange={(e) => setReplyBody(e.target.value)}
                rows={8}
              />
              <Button variant="outline" size="sm" className="gap-1" onClick={generateAiReply} disabled={generatingReply}>
                {generatingReply ? <Loader2 className="h-3 w-3 animate-spin" /> : <Sparkles className="h-3 w-3" />}
                {generatingReply ? "Grafton rédige…" : "Rédiger avec Grafton IA"}
              </Button>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => { setReplyDialog(false); setReplyBody(""); }}>Annuler</Button>
            <Button onClick={handleReply} disabled={!replyBody.trim()} className="gap-2">
              {replyMode === "email" ? <Send className="h-4 w-4" /> : <Printer className="h-4 w-4" />}
              {replyMode === "email" ? "Ouvrir dans le client email" : "Générer le PDF"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Classify dialog */}
      <Dialog open={classifyDialog} onOpenChange={setClassifyDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Archive className="h-5 w-5 text-primary" />
              Classer dans un dossier
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-1">
            {folders.map((folder) => (
              <button
                key={folder.id}
                onClick={() => classifyDoc(folder.id)}
                className="w-full flex items-center gap-3 px-4 py-3 rounded-lg hover:bg-secondary/50 transition-colors text-left"
              >
                <div className="h-3 w-3 rounded-full" style={{ backgroundColor: folder.color }} />
                <span className="text-sm text-foreground">{folder.name}</span>
                <span className="text-xs text-muted-foreground ml-auto">
                  {documents.filter((d) => d.folder_id === folder.id).length} courrier(s)
                </span>
              </button>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      {/* Recommandé dialog */}
      <Dialog open={recommandeDialog} onOpenChange={setRecommandeDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Shield className="h-5 w-5 text-primary" />
              Envoyer un recommandé
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="p-4 rounded-lg bg-primary/5 border border-primary/20">
              <div className="flex items-center gap-2 mb-3">
                <MailOpen className="h-5 w-5 text-primary" />
                <span className="text-sm font-medium text-primary">Maileva (La Poste)</span>
              </div>
              <p className="text-sm text-foreground/80 mb-3">
                Envoyez vos courriers recommandés directement depuis Grafton via Maileva, le service officiel de La Poste.
              </p>
              <div className="space-y-2 text-sm text-muted-foreground">
                <div className="flex items-center gap-2">
                  <Check className="h-4 w-4 text-green-500" />
                  <span>Recommandé avec accusé de réception</span>
                </div>
                <div className="flex items-center gap-2">
                  <Check className="h-4 w-4 text-green-500" />
                  <span>Valeur juridique identique au courrier postal</span>
                </div>
                <div className="flex items-center gap-2">
                  <Check className="h-4 w-4 text-green-500" />
                  <span>Suivi en temps réel de l'acheminement</span>
                </div>
                <div className="flex items-center gap-2">
                  <Check className="h-4 w-4 text-green-500" />
                  <span>Impression, mise sous pli et envoi par La Poste</span>
                </div>
              </div>
            </div>
            <div className="p-3 rounded-lg bg-amber-500/10 border border-amber-500/20">
              <p className="text-sm text-amber-700 dark:text-amber-400">
                <strong>Intégration Maileva en cours de déploiement.</strong> En attendant, vous pouvez générer un PDF prêt à imprimer via le bouton "Répondre par courrier".
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRecommandeDialog(false)}>Fermer</Button>
            <Button onClick={() => {
              setRecommandeDialog(false);
              window.open("https://www.maileva.com", "_blank");
            }} className="gap-2">
              <Send className="h-4 w-4" />
              Accéder à Maileva
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </SidebarProvider>
  );
};

export default AdminDashboard;
